import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';

interface WizardNavBarProps {
  currentStep: number;
  totalSteps: number;
  submitLabel: string;
  isCurrentStepValid: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onGoToStep: (step: number) => void;
}

const WizardNavBar: React.FC<WizardNavBarProps> = ({
  currentStep,
  totalSteps,
  submitLabel,
  isCurrentStepValid,
  isSubmitting,
  onBack,
  onNext,
  onSubmit,
  onGoToStep,
}) => {
  return (
    <nav
      className="shrink-0 flex items-center justify-between px-4 h-[60px]
                 bg-[var(--nav-frosted)] backdrop-blur-md
                 border-t border-[var(--nav-border)]
                 safe-area-bottom"
      aria-label="Wizard navigation"
    >
      {/* Back button */}
      <button
        data-track="wizard_back"
        onClick={onBack}
        className={`flex items-center justify-center min-h-[48px] min-w-[48px]
                    rounded-lg text-body hover:text-heading hover:bg-surface-hover
                    transition-colors duration-150
                    ${currentStep === 1 ? 'invisible' : ''}`}
        aria-label="Previous step"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>

      {/* Progress bar */}
      <div
        className="flex items-center gap-2"
        role="navigation"
        aria-label="Wizard progress"
      >
        <button
          data-track="wizard_step_back"
          className="flex items-center h-11 cursor-pointer"
          aria-label={`Step ${currentStep} of ${totalSteps}. Tap to go back.`}
          onClick={() => {
            if (currentStep > 1) onGoToStep(currentStep - 1);
          }}
        >
          {/* 8px bar inside a 44px-tall hit area */}
          <span className="relative block w-[120px] h-2 rounded-full bg-stone-200 dark:bg-stone-700 overflow-hidden">
            <span
              className="absolute inset-y-0 left-0 rounded-full bg-primary-500
                          transition-all duration-300 ease-out
                          motion-reduce:transition-none"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </span>
        </button>
        <span
          className="text-xs font-medium text-subtle tabular-nums"
          aria-current="step"
          aria-label={`Step ${currentStep} of ${totalSteps}`}
        >
          {currentStep}/{totalSteps}
        </span>
      </div>

      {/* Next / Submit button */}
      {currentStep < totalSteps ? (
        <button
          data-track="wizard_next"
          onClick={onNext}
          disabled={!isCurrentStepValid}
          className={`flex items-center gap-1 min-h-[48px] px-5
                      rounded-lg font-medium text-sm
                      transition-all duration-150
                      ${isCurrentStepValid
                        ? 'bg-primary-700 text-white shadow-sm hover:bg-primary-800 active:scale-[0.97]'
                        : 'bg-stone-200 text-stone-400 dark:bg-stone-700 dark:text-stone-500 cursor-not-allowed'
                      }
                      motion-reduce:active:scale-100`}
          aria-label="Next step"
        >
          Next
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      ) : (
        <button
          data-track="wizard_submit"
          onClick={onSubmit}
          disabled={!isCurrentStepValid || isSubmitting}
          className="flex items-center gap-1.5 min-h-[48px] px-5
                     rounded-lg font-medium text-sm
                     bg-primary-700 text-white shadow-sm
                     hover:bg-primary-800 active:scale-[0.97]
                     disabled:bg-stone-200 disabled:text-stone-400
                     dark:disabled:bg-stone-700 dark:disabled:text-stone-500
                     disabled:cursor-not-allowed disabled:shadow-none
                     motion-reduce:active:scale-100
                     transition-all duration-150"
          aria-label={submitLabel}
        >
          {isSubmitting ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white
                             rounded-full animate-spin" />
          ) : (
            <PlayIcon className="h-4 w-4" />
          )}
          {submitLabel}
        </button>
      )}
    </nav>
  );
};

export default WizardNavBar;
